import React, { useState, useEffect, useContext } from 'react';
import { Card, Row, Col, Button, Spin, Alert, message, Switch, Typography, Collapse } from 'antd';
import { StarOutlined, CrownOutlined, ThunderboltOutlined, RocketOutlined, TrophyOutlined, DollarOutlined, SmileOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useRazorpay from 'react-razorpay';
import { ThemeContext } from '../../context/ThemeContext';
import paymentService from '../../services/paymentService';
import LandingLayout from '../LandingPagesRedesign/LandingLayoutRedesign';
import './PricingPage.css';

const { Title, Paragraph } = Typography;

const PricingPage = ({ skipLayout = false }) => {
  const [Razorpay] = useRazorpay();
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [isYearly, setIsYearly] = useState(false);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [expandedItems, setExpandedItems] = useState(['1']);

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    filterPlans();
  }, [plans, isYearly]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await paymentService.getPlans();
      if (response.statusCode === 200) {
        setPlans(response.data);
      } else {
        setError('Failed to fetch plans');
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to fetch subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const filterPlans = () => {
    if (!plans.length) return;
    
    const filtered = plans.filter(plan => {
      if (isYearly) {
        return plan.planId.includes('yearly') || plan.duration.includes('year');
      } else {
        return plan.planId.includes('monthly') || plan.duration.includes('month');
      }
    });
    
    setFilteredPlans(filtered);
  };

  const createOrder = async (plan) => {
    try {
      setProcessing(true);
      
      const response = await paymentService.createOrder({
        planId: plan.planId,
        amount: plan.price,
        currency: plan.currency,
        customerName: localStorage.getItem('userName') || 'User',
        customerEmail: localStorage.getItem('userEmail') || 'user@example.com',
        customerPhone: localStorage.getItem('userPhone') || '9999999999',
        notes: `Subscription to ${plan.planName}`
      });

      if (response.statusCode === 200) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create order');
      }
    } catch (err) {
      console.error('Error creating order:', err);
      message.error('Failed to create order. Please try again.');
      throw err;
    } finally {
      setProcessing(false);
    }
  };

  const verifyPayment = async (paymentData) => {
    try {
      const response = await paymentService.verifyPayment({
        razorpayOrderId: paymentData.razorpay_order_id,
        razorpayPaymentId: paymentData.razorpay_payment_id,
        razorpaySignature: paymentData.razorpay_signature
      });

      if (response.statusCode === 200 && response.data.success) {
        message.success('Payment successful! Your subscription is now active.');
        // Redirect to dashboard or show success page
        window.location.href = '/my-portfolio';
      } else {
        message.error('Payment verification failed. Please contact support.');
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
      message.error('Payment verification failed. Please contact support.');
    }
  };

  const checkAuthentication = () => {
    const hasUser = !!localStorage.getItem('userEmail');
    if (!hasUser) {
      message.warning('Please login to continue with payment');
      navigate('/login');
      return false;
    }
    return true;
  };

  const handlePayment = async (plan) => {
    // Check authentication before proceeding with payment
    if (!checkAuthentication()) {
      return;
    }

    try {
      const orderData = await createOrder(plan);
      
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'SimplyFI',
        description: `Subscription to ${plan.planName}`,
        image: '/logo.png',
        order_id: orderData.razorpayOrderId,
        handler: (response) => {
          verifyPayment(response);
        },
        prefill: {
          name: localStorage.getItem('userName') || 'User',
          email: localStorage.getItem('userEmail') || 'user@example.com',
          contact: localStorage.getItem('userPhone') || '9999999999'
        },
        notes: {
          plan: plan.planName,
          planId: plan.planId
        },
        theme: {
          color: '#3399cc'
        },
        modal: {
          ondismiss: () => {
            message.info('Payment cancelled');
          }
        }
      };

      const rzp = new Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Payment error:', err);
    }
  };

  const getPlanIcon = (planName) => {
    if (planName.toLowerCase().includes('basic')) {
      return <ThunderboltOutlined className="plan-icon" />;
    } else if (planName.toLowerCase().includes('premium')) {
      return <RocketOutlined className="plan-icon" />;
    } else if (planName.toLowerCase().includes('enterprise')) {
      return <TrophyOutlined className="plan-icon" />;
    }
    return <ThunderboltOutlined className="plan-icon" />;
  };


  const getPlanFeatures = (plan) => {
    try {
      return JSON.parse(plan.features || '[]');
    } catch {
      return ['Basic features', 'Email support'];
    }
  };

  const faqData = {
    general: [
      {
        id: '1',
        question: 'Can I change my plan anytime?',
        answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.'
      },
      {
        id: '2',
        question: 'Is there a free trial?',
        answer: 'Yes, we offer a 14-day free trial for all plans. No credit card required.'
      },
      {
        id: '3',
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards, debit cards, and UPI payments through Razorpay.'
      },
      {
        id: '4',
        question: 'Can I cancel anytime?',
        answer: 'Yes, you can cancel your subscription anytime. No cancellation fees or hidden charges.'
      }
    ],
    pricing: [
      {
        id: '1',
        question: 'How does billing work?',
        answer: 'Billing is done monthly or yearly based on your chosen plan. You can switch between billing cycles anytime.'
      },
      {
        id: '2',
        question: 'Are there any hidden fees?',
        answer: 'No hidden fees. The price you see is exactly what you pay, including all taxes.'
      }
    ],
    dashboard: [
      {
        id: '1',
        question: 'How do I access my dashboard?',
        answer: 'Once you subscribe, you can access your dashboard immediately through the login portal.'
      },
      {
        id: '2',
        question: 'Can I customize my dashboard?',
        answer: 'Yes, you can customize widgets, layouts, and preferences to match your workflow.'
      }
    ],
    api: [
      {
        id: '1',
        question: 'Do you provide API access?',
        answer: 'Yes, API access is available for Premium plan subscribers with comprehensive documentation.'
      },
      {
        id: '2',
        question: 'What are the API rate limits?',
        answer: 'Rate limits vary by plan. Premium users get higher limits and priority access.'
      }
    ]
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedItems([]);
  };

  const handleExpand = (itemId) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  if (loading) {
    return (
      <div className="pricing-container">
        <div className="loading-container">
          <Spin size="large" />
          <p>Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pricing-container">
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={fetchPlans}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  const content = (
    <div className="pricing-page">
      <div className="container">
        {/* Header Section */}
        <div className="pricing-header">
          <Title level={1} className="main-title">
            <DollarOutlined className="title-icon" />
            Pricing Plans
          </Title>
          <Paragraph className="subtitle">
            Simple, transparent pricing that grows with you. Choose the perfect plan for your investment needs.
          </Paragraph>
        </div>


      {/* Simple Toggle Section */}
      <div className="pricing-toggle-section">
        <div className="simple-toggle-container">
          <div className="toggle-buttons">
            <button 
              className={`toggle-btn ${!isYearly ? 'selected' : ''}`}
              onClick={() => setIsYearly(false)}
            >
              Monthly
            </button>
            <button 
              className={`toggle-btn ${isYearly ? 'selected' : ''}`}
              onClick={() => setIsYearly(true)}
            >
              Yearly
              <span className="save-badge">Save 20%</span>
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="pricing-cards-container">
        <Row gutter={[32, 32]} className="pricing-cards">
          {filteredPlans.map((plan, index) => (
            <Col xs={24} sm={12} lg={8} key={plan.planId}>
              <Card className="pricing-card">
                {/* Top Section */}
                <div className="plan-top-section">
                  <h3 className="plan-name">{plan.planName}</h3>
                  <div className="plan-price">
                    <span className="price-amount">₹{plan.price}</span>
                    <span className="price-period">per month</span>
                  </div>
                  <p className="plan-description">{plan.description || `Features for ${plan.planName.toLowerCase()}`}</p>
                  <Button 
                    className="get-started-btn" 
                    onClick={() => handlePayment(plan)}
                    loading={processing}
                    disabled={!plan.active}
                  >
                    {plan.active ? 'Get Started' : 'Coming Soon'}
                  </Button>
                </div>
                
                {/* Bottom Section */}
                <div className="plan-bottom-section">
                  <h4 className="features-heading">FEATURES</h4>
                  <p className="features-intro">Everything in our plan includes</p>
                  <ul className="features-list">
                    {getPlanFeatures(plan).map((feature, idx) => (
                      <li key={idx} className="feature-item">
                        <span className="checkmark">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Features Comparison */}
      <div className="features-comparison">
        <h2 className="comparison-title">Why choose SimplyFI?</h2>
        <Row gutter={[24, 24]} className="comparison-features">
          <Col xs={24} sm={8}>
            <div className="comparison-item">
              <div className="comparison-icon">
                <ThunderboltOutlined />
              </div>
              <h3>Real-time data</h3>
              <p>Get instant access to live bond market data and analytics</p>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="comparison-item">
              <div className="comparison-icon">
                <RocketOutlined />
              </div>
              <h3>Advanced Analytics</h3>
              <p>Powerful tools to analyze market trends and make informed decisions</p>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="comparison-item">
              <div className="comparison-icon">
                <TrophyOutlined />
              </div>
              <h3>Premium Support</h3>
              <p>24/7 expert support to help you succeed in the bond market</p>
            </div>
          </Col>
        </Row>
      </div>

      {/* FAQ Section */}
      <div className="pricing-faq">
        <h2 className="faq-title">Frequently Asked Questions</h2>
        
        {/* FAQ Tabs */}
        <div className="faq-tabs">
          <button 
            className={`faq-tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => handleTabChange('general')}
          >
            General
          </button>
          <button 
            className={`faq-tab ${activeTab === 'pricing' ? 'active' : ''}`}
            onClick={() => handleTabChange('pricing')}
          >
            Pricing
          </button>
          <button 
            className={`faq-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleTabChange('dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`faq-tab ${activeTab === 'api' ? 'active' : ''}`}
            onClick={() => handleTabChange('api')}
          >
            API
          </button>
        </div>

        {/* FAQ Items */}
        <div className="faq-items">
          {faqData[activeTab].map((item) => (
            <div key={item.id} className="faq-item">
              <div className="faq-item-header" onClick={() => handleExpand(item.id)}>
                <div className="faq-item-icon">
                  <SmileOutlined />
                </div>
                <h4 className="faq-question">{item.question}</h4>
                <div className="faq-expand-icon">
                  {expandedItems.includes(item.id) ? <UpOutlined /> : <DownOutlined />}
                </div>
              </div>
              {expandedItems.includes(item.id) && (
                <div className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );

  return skipLayout ? (
    content
  ) : (
    <LandingLayout>
      {content}
    </LandingLayout>
  );
};

export default PricingPage;
